import React, { useState, useEffect } from "react";

import {
  Button,
  IconButton,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  TextField as MuiTextField,
  Divider as MuiDivider,
  Paper,
  Chip,
  Box,
  Popover,
  Card,
  CardActions,
} from "@mui/material";
import styled from "@emotion/styled";
import EditIcon from "@mui/icons-material/Edit";
import { Add as AddIcon } from "@mui/icons-material";
import { spacing } from "@mui/system";
import * as Yup from "yup";
import { useFormik, Form, FormikProvider } from "formik";
import useReferentiel from "hooks/useReferentiel";
import useNotification from "hooks/useNotification";
import { AGES, GENDERS, RULE_OPERATORS } from "config";
import { getLabelOperator, getLabelValues } from "utils/ruleUtil";
import RuleDataForm from "./RuleDataForm";
// ----------------------------------------------------------------------
export default function ProfileRuleForm({ editData, onComplete }) {
  const refName = "profilerule";
  // ----------------------------------------------------------------------
  const { refs, saveRef, initRefs } = useReferentiel();
  const { notif } = useNotification();

  // ----------------------------------------------------------------------
  const handleSave = async (data, field) => {
    try {
      const newData = { ...editData };
      newData[field] = data;
      await saveRef(
        {
          ...newData,
        },
        refName
      );
      await initRefs();
      notif("Sauvegarde réussie");
    } catch (error) {
      console.error(error);
      notif(null, error);
    }
  };
  // ----------------------------------------------------------------------

  const getArr = () => {
    if (!editData) return [];
    const arr = [
      {
        field: "gender",
        label: "Genre",
        refData: GENDERS,
        item: editData.gender,
        formEdit: (
          <RuleDataForm
            editData={editData.gender}
            handleSave={handleSave}
            field="gender"
            refData={GENDERS}
            typeRule="stringType"
            fieldLabel="Genre"
          />
        ),
      },
      {
        field: "ages",
        label: "Ages",
        item: editData.ages,
        refData: AGES,
        formEdit: (
          <RuleDataForm
            editData={editData.ages}
            handleSave={handleSave}
            field="ages"
            refData={AGES}
            typeRule="arrayType"
            fieldLabel="Age"
          />
        ),
      },
    ];
    return arr;
  };

  // ----------------------------------------------------------------------

  return (
    <>
      <FormEdit editData={editData} onComplete={onComplete} />
      <Divider my={3} />
      {editData && (
        <>
          {getArr().map((it, i) => {
            const { refData, field, label, formEdit, item } = it;
            const operatorLabel = getLabelOperator(item);
            const operatorValues = getLabelValues(refData, item);
            return (
              <ItemPaper square={false} key={i}>
                <Box
                  display="flex"
                  sx={{ width: "100%" }}
                  justifyContent="flex-start"
                  alignItems="center"
                >
                  <RuleEditBtn
                    editData={editData[field]}
                    handleSave={handleSave}
                    field={field}
                    refData={refData}
                    formEdit={formEdit}
                  />
                  <h5>
                    {label}&nbsp;
                    {operatorLabel && (
                      <span style={{ color: "red" }}>{operatorLabel} </span>
                    )}{" "}
                  </h5>
                  &nbsp;
                  {operatorValues}
                </Box>
              </ItemPaper>
            );
          })}
        </>
      )}
    </>
  );
}
// ----------------------------------------------------------------------

const Divider = styled(MuiDivider)(spacing);

// ----------------------------------------------------------------------
const TextField = styled(MuiTextField)(spacing);
// ----------------------------------------------------------------------
const ItemPaper = styled(Paper)(({ theme }) => ({
  width: "100%",
  height: "auto",
  padding: theme.spacing(2),
  ...theme.typography.body2,
  textAlign: "flex-start",
}));
// ----------------------------------------------------------------------
const FormEdit = ({ editData, onComplete }) => {
  const refName = "profilerule";

  // ----------------------------------------------------------------------

  const { saveRef, initRefs } = useReferentiel();
  const { notif } = useNotification();
  // ----------------------------------------------------------------------

  const dataSchema = Yup.object().shape({
    code: Yup.string().max(20).required("Veuillez renseigner le code"),
    label: Yup.string().max(50).required(`Veuillez renseigner le libellé`),
    profileDescription: Yup.string()
      .max(250)
      .required(`Veuillez renseigner la description`),
    profileHelp: Yup.string()
      .max(200)
      .required(`Veuillez renseigner l'aide du profiling`),
  });
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      code: editData && editData.code ? editData.code : "",
      label: editData && editData.label ? editData.label : "",
      profileDescription:
        editData && editData.profileDescription
          ? editData.profileDescription
          : "",
      profileHelp: editData && editData.profileHelp ? editData.profileHelp : "",
    },
    validationSchema: dataSchema,
    onSubmit: async () => {
      try {
        const newData =
          editData && editData.id ? { ...editData, ...values } : { ...values };
        await saveRef(
          {
            ...newData,
          },
          refName
        );
        await initRefs();
        notif("Sauvegarde réussie");
        onComplete();
      } catch (error) {
        console.error(error);
        notif(null, error);
        setSubmitting(false);
      }
    },
  });
  const {
    errors,
    touched,
    values,
    isSubmitting,
    handleSubmit,
    getFieldProps,
    setSubmitting,
  } = formik;
  if (!refName) return <></>;

  return (
    <>
      <FormikProvider value={formik}>
        <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
          <TextField
            fullWidth
            type="text"
            label="Code"
            {...getFieldProps("code")}
            error={Boolean(touched.code && errors.code)}
            helperText={touched.code && errors.code}
            my={2}
          />
          <TextField
            fullWidth
            type="text"
            name="label"
            label="Libellé"
            {...getFieldProps("label")}
            error={Boolean(touched.label && errors.label)}
            helperText={touched.label && errors.label}
            my={2}
          />
          <TextField
            fullWidth
            type="text"
            name="profileDescription"
            label="Description du profil"
            {...getFieldProps("profileDescription")}
            error={Boolean(
              touched.profileDescription && errors.profileDescription
            )}
            helperText={touched.profileDescription && errors.profileDescription}
            my={2}
          />
          <TextField
            fullWidth
            type="text"
            name="profileHelp"
            label="Texte d'aide"
            {...getFieldProps("profileHelp")}
            error={Boolean(touched.profileHelp && errors.profileHelp)}
            helperText={touched.profileHelp && errors.profileHelp}
            my={2}
          />
          <Button
            sx={{ my: 2 }}
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            disabled={isSubmitting}
          >
            Valider
          </Button>
        </Form>
      </FormikProvider>
    </>
  );
};

// ----------------------------------------------------------------------

const RuleEditBtn = ({ formEdit }) => {
  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  return (
    <div>
      <Button
        aria-label="details"
        size="small"
        onClick={handleClick}
        color="primary"
      >
        <EditIcon /> Modifier
      </Button>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <Card
          sx={{
            minWidth: 575,
            p: 4,
          }}
        >
          {formEdit}
          <CardActions sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button onClick={handleClose}>Annuler</Button>
          </CardActions>
        </Card>
      </Popover>
    </div>
  );
};
